// Every look there is, found rather than listed (LOOKS.md Part 9): each folder
// under `looks/` with an `index.ts` is one. Dropping a folder in makes a look
// the picker offers, the account menu cycles through and the tests hold to the
// contract; taking one out retires it, and anybody who had it chosen reads as
// Clean.

import { DEFAULT_LOOK, type LookId } from '@fsp/theme';
import type { Look } from './define';

const found = import.meta.glob<{ default: Look }>('./*/index.ts', { eager: true });

function load(): Look[] {
  const looks = Object.entries(found).map(([path, module]) => {
    const folder = path.split('/')[1]!;
    const look = module.default;
    // Fail loudly: a look whose id is not its folder's name would store one
    // value and look for its stylesheet under another.
    if (look.id !== folder) {
      throw new Error(`The look in looks/${folder}/ calls itself '${look.id}'.`);
    }
    if (!/^[a-z][a-z-]*$/.test(look.id)) {
      throw new Error(`A look's id is lowercase letters and dashes: '${look.id}'.`);
    }
    return look;
  });
  if (!looks.some((l) => l.id === DEFAULT_LOOK)) {
    throw new Error(`The default look '${DEFAULT_LOOK}' has no folder.`);
  }
  return looks.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
}

/** Every look, in the picker's order. */
export const LOOKS: readonly Look[] = load();

const BY_ID = new Map(LOOKS.map((l) => [l.id, l]));

/** A look by id; an id this build does not know is Clean. */
export function lookOf(id: LookId): Look {
  return BY_ID.get(id) ?? BY_ID.get(DEFAULT_LOOK)!;
}

/** A stored value this build does not know — a retired look, a typo — reads as the default. */
export function parseLook(raw: unknown): LookId {
  return typeof raw === 'string' && BY_ID.has(raw) ? raw : DEFAULT_LOOK;
}
